<template>
  <v-dialog v-model="show" max-width="500px" persistent @keydown="onKeydown">
    <v-card class="pa-2">
      <v-card-title class="font">
        Действия с заявкой

        <v-spacer />

        <v-btn icon @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" show-arrows>
        <v-tab href="#external-sync">Синхронизация</v-tab>
        <v-tab href="#restart">Перезапуск</v-tab>
        <v-tab href="#misc">Прочее</v-tab>
      </v-tabs>

      <v-card-text class="my-3">
        <v-tabs-items v-model="tab">
          <v-tab-item value="external-sync">
            <v-btn
              block
              :elevation="0"
              @click="requestExternalSync('crmAndAbs')"
              class="item-button"
              color="secondary"
            >
              CRM и АБС
            </v-btn>

            <v-btn
              block
              :elevation="0"
              @click="requestExternalSync('portal')"
              class="item-button"
              color="secondary"
            >
              Портал
            </v-btn>
          </v-tab-item>

          <v-tab-item value="restart">
            <AppRestart/>
          </v-tab-item>

          <v-tab-item value="misc">
            <v-btn
              block
              :elevation="0"
              @click="requireConfirmModal(retryOrchestratorProcess)"
              class="item-button"
              color="secondary"
            >
              Повторить текущий шаг
            </v-btn>

            <v-btn
              block
              :elevation="0"
              @click="showChangePhoneNumberModal = true"
              class="item-button"
              color="secondary"
            >
              Смена номера телефона
            </v-btn>

            <v-btn
              block
              :elevation="0"
              @click="requireConfirmModal(sendOrchestratorArchive)"
              class="item-button"
              color="secondary"
            >
              Отправить в архив
            </v-btn>

            <template v-if="model && model.lkstatus === 'okved.wait'">
              <span class="text-overline">Отказ/одобрение ОКВЭД</span>
              <v-btn
                block
                :elevation="0"
                @click="requireConfirmModal(sendOkvedDecision.bind(null, 'decline'))"
                class="item-button"
                color="warning"
              >Отказ по ОКВЭД</v-btn>

              <v-btn
                block
                :elevation="0"
                @click="requireConfirmModal(sendOkvedDecision.bind(null, 'success'))"
                class="item-button"
                color="warning lighten-2"
              >Одобрить по ОКВЭД</v-btn>
            </template>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" :timeout="3000" top color="success">
      {{ message }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Закрыть</v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="showChangePhoneNumberModal" max-width="500px">
      <v-card>
        <v-card-title>
          Смена номера телефона
        </v-card-title>
        <v-card-text>
          <span>Текущий: {{ currentPhoneNumber }}</span>
          <v-form v-model="formInputPhoneNumberValid">
            <v-text-field
              v-mask="'+7(###) ###-##-##'"
              dense
              single-line
              outlined
              v-model="inputPhoneNumber"
              label="Введите новый номер"
              :rules="phoneRules"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="justify-end pt-1">
          <v-btn
            color="success"
            :elevation="0"
            @click.stop="showChangePhoneNumberModal = false"
            class="mr-3"
          >
            Закрыть
          </v-btn>
          <v-btn
            color="warning"
            :elevation="0"
            :disabled="!inputPhoneNumber || !formInputPhoneNumberValid"
            @click="setOrchestratorPhone"
          >
            Сменить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showConfirmModal" max-width="500px">
      <v-card>
        <v-card-title>
          Вы уверены?
        </v-card-title>
        <v-card-text>
          Подтвердите что вы хотите выполнить эту операцию.
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="success"
            :elevation="0"
            @click="showConfirmModal = false;"
            class="mr-3"
          >
            Закрыть
          </v-btn>
          <v-spacer />
          <v-btn
            color="warning"
            :elevation="0"
            @click="onConfirmModal(); showConfirmModal = false;"
          >
            Продолжить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import { mask } from "vue-the-mask";
import { required, phone } from "@/utils/rules";
import AppRestart from "@/components/apps/AppRestart.vue";

export default {
  name: "AppActionsModal",

  directives: {
    mask
  },

  components: {
    AppRestart
  },

  props: {
    value: Boolean
  },

  computed: {
    ...mapState("app", ["logs"]),
    ...mapGetters("app", ["model"]),

    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },

  data: () => ({
    tab: null,
    snackbar: false,
    message: null,

    showChangePhoneNumberModal: false,
    inputPhoneNumber: '',
    phoneRules: [required('Требуется ввести номер телефона'), phone],
    currentPhoneNumber: null,
    formInputPhoneNumberValid: false,

    showConfirmModal: false,
    onConfirmModal: null
  }),

  mounted() {
    this.currentPhoneNumber = this.model?.ul?.phoneNumber ?? 'Н/Д';
  },

  watch: {
    showChangePhoneNumberModal(v) {
      if (!v) {
        this.inputPhoneNumber = '';
      }
    },

    showConfirmModal(v) {
      if (!v) {
        this.onConfirmModal = null;
      }
    }
  },

  methods: {
    ...mapActions("app", [
      "appRequestExternalSynchronization",
      "appRetryOrchestratorProcess",
      "appSetOrchestratorPhone",
      "appSendOrchestratorArchive",
      "appSendOkvedDecision"
    ]),

    onKeydown(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    showSnackbar(message) {
      this.message = message || 'Запрос отправлен';
      this.snackbar = true;
    },

    async requestExternalSync(type) {
      await this.appRequestExternalSynchronization(type);
      if (res) {
        this.showSnackbar();
      }
    },

    async retryOrchestratorProcess() {
      const res = await this.appRetryOrchestratorProcess();
      if (res) {
        this.showSnackbar();
      }
    },

    async setOrchestratorPhone() {
      const res = await this.appSetOrchestratorPhone(this.inputPhoneNumber);
      if (res) {
        this.showSnackbar();
      }
    },

    async sendOrchestratorArchive() {
      const res = await this.appSendOrchestratorArchive();
      if (res) {
        this.showSnackbar();
      }
    },

    async sendOkvedDecision(decision) {
      const res = await this.appSendOkvedDecision(decision);
      if (res) {
        this.showSnackbar();
      }
    },

    requireConfirmModal(callback) {
      this.showConfirmModal = true;
      this.onConfirmModal = callback.bind(this);
    }
  },
}
</script>

<style lang="scss" scoped>
.item-button:not(:last-child) {
  margin-bottom: 1.5em;
}
</style>
